import { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import getConsultaJson from '../../service/getConsultaJson';

interface Props {
    consultaId: string;
}

type JsonKey = 'request' | 'response' | 'requestApi' | 'responseApi';

const LABELS: Record<JsonKey, string> = {
    request: 'Request',
    response: 'Response',
    requestApi: 'Request API (Fornecedor)',
    responseApi: 'Response API (Fornecedor)',
};

function tryFormatJson(value: string | null | undefined): string {
    if (!value) return '';
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
}

export default function ConsultaJsonModal({ consultaId }: Props) {
    const menuRef = useRef<Menu>(null);
    const toast = useRef<Toast>(null);
    const [visible, setVisible] = useState(false);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);

    const openJson = (key: JsonKey) => {
        setLoading(true);
        setTitle(LABELS[key]);
        setContent('');
        setVisible(true);
        getConsultaJson(consultaId)
            .then((data: any) => {
                setContent(tryFormatJson(data[key]));
            })
            .catch(() => {
                setContent('Erro ao carregar dados.');
            })
            .finally(() => setLoading(false));
    };

    const menuItems = (Object.keys(LABELS) as JsonKey[]).map((key) => ({
        label: LABELS[key],
        icon: 'pi pi-code',
        command: () => openJson(key),
    }));

    const handleCopy = () => {
        navigator.clipboard.writeText(content).then(() => {
            toast.current?.show({ severity: 'success', summary: 'Copiado', detail: 'JSON copiado para área de transferência', life: 2000 });
        });
    };

    const handleDownload = () => {
        const blob = new Blob([content], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/\s/g, '_').toLowerCase()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const footer = (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button label="Copiar" icon="pi pi-copy" onClick={handleCopy} severity="secondary" disabled={!content} />
            <Button label="Download" style={{backgroundColor:'#183462'}} icon="pi pi-download" onClick={handleDownload} disabled={!content} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Menu model={menuItems} popup ref={menuRef} />
            <Button
                icon="pi pi-ellipsis-v"
                onClick={(e) => menuRef.current?.toggle(e)}
                style={{ background: 'transparent', color: 'gray', border: 'none' }}
                aria-label="Ver JSONs"
            />
            <Dialog
                header={title}
                visible={visible}
                style={{ width: '70vw' }}
                onHide={() => setVisible(false)}
                footer={footer}
                maximizable
            >
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                    </div>
                ) : (
                    <pre
                        style={{
                            background: '#1e1e1e',
                            color: '#d4d4d4',
                            padding: '1rem',
                            borderRadius: '6px',
                            overflow: 'auto',
                            maxHeight: '65vh',
                            fontSize: '0.85rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                        }}
                    >
                        {content || <span style={{ color: '#888' }}>Sem dados disponíveis</span>}
                    </pre>
                )}
            </Dialog>
        </>
    );
}
